import type { FormEvent, JSX } from 'react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input } from '@artosera/ui';
import { ApiError, estErreurStepUpRequis, verifierDeuxFacteurs } from '../lib/api.js';
import { ElevationAnnulee, ElevationNonAcceptee, estElevationAnnulee } from '../lib/elevation.js';
import formStyles from '../styles/form.module.css';

export { ElevationAnnulee, estElevationAnnulee };

/**
 * Élévation du second facteur dans la console (ARTO-58). `POST` et `PATCH
 * /v1/console/associes`, et `DELETE …/associes/:id/invitation`, exigent une
 * vérification récente du second facteur
 * (`exigerMfaRecente`, quinze minutes) : l'API répond `403 acces.refuse`,
 * `details.raison = mfa_step_up_requis`. Le modèle est celui de la régie
 * (`routes/coffre/StepUpDialog.tsx`) : la boîte s'ouvre, un code accepté la
 * referme et le geste d'origine est REJOUÉ — jamais une relance silencieuse.
 */

interface Attente {
  resolve: () => void;
  reject: (erreur: Error) => void;
}

/**
 * `executer(geste)` lance le geste ; si l'API demande l'élévation, la boîte
 * s'ouvre (à rendre via `dialogue`), puis le geste est rejoué UNE fois. Une
 * annulation rejette avec `ElevationAnnulee`, que l'appelant ignore.
 */
export function useElevation(): {
  executer: <T>(geste: () => Promise<T>) => Promise<T>;
  dialogue: JSX.Element;
  /** La boîte est ouverte : une autre boîte modale s'efface le temps de la vérification. */
  enElevation: boolean;
} {
  const [ouvert, setOuvert] = useState(false);
  const attente = useRef<Attente | null>(null);

  const demander = useCallback(
    (): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        attente.current = { resolve, reject };
        setOuvert(true);
      }),
    [],
  );

  const executer = useCallback(
    async <T,>(geste: () => Promise<T>): Promise<T> => {
      try {
        return await geste();
      } catch (erreur) {
        if (!estErreurStepUpRequis(erreur)) throw erreur;
        await demander();
        try {
          return await geste();
        } catch (refus) {
          // Refusé de nouveau juste après une vérification acceptée : on ne
          // rouvre pas la boîte en boucle, on le dit.
          if (estErreurStepUpRequis(refus)) throw new ElevationNonAcceptee();
          throw refus;
        }
      }
    },
    [demander],
  );

  const terminer = (verifie: boolean): void => {
    const courante = attente.current;
    attente.current = null;
    setOuvert(false);
    if (verifie) courante?.resolve();
    else courante?.reject(new ElevationAnnulee());
  };

  return {
    executer,
    enElevation: ouvert,
    dialogue: (
      <ElevationDialog
        ouvert={ouvert}
        onVerifie={() => terminer(true)}
        onAnnule={() => terminer(false)}
      />
    ),
  };
}

function ElevationDialog({
  ouvert,
  onVerifie,
  onAnnule,
}: {
  ouvert: boolean;
  onVerifie: () => void;
  onAnnule: () => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const [code, setCode] = useState('');
  // Un code de secours à la place du code de l'application (comme la connexion).
  const [secours, setSecours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const verifier = async (evenement?: FormEvent): Promise<void> => {
    evenement?.preventDefault();
    if (code.trim() === '') return;
    setErreur(null);
    setEnCours(true);
    try {
      await verifierDeuxFacteurs(code.trim(), secours);
      setCode('');
      onVerifie();
    } catch (echec) {
      setErreur(echec instanceof ApiError ? echec.message : t('elevation.echec'));
    } finally {
      setEnCours(false);
    }
  };

  const basculer = (): void => {
    setSecours((courant) => !courant);
    setCode('');
    setErreur(null);
  };

  const annuler = (): void => {
    setCode('');
    setSecours(false);
    setErreur(null);
    onAnnule();
  };

  return (
    <Dialog
      open={ouvert}
      onOpenChange={(estOuvert) => {
        if (!estOuvert) annuler();
      }}
      title={t('elevation.titre')}
      description={t('elevation.description')}
      closeLabel={t('elevation.fermer')}
      footer={
        <>
          <Button variant="ghost" onClick={annuler}>
            {t('elevation.annuler')}
          </Button>
          <Button onClick={() => void verifier()} loading={enCours} disabled={code.trim() === ''}>
            {t('elevation.verifier')}
          </Button>
        </>
      }
    >
      <form className={formStyles.champ} onSubmit={(e) => void verifier(e)} noValidate>
        <label htmlFor="elevation-code">
          {secours ? t('elevation.champSecours') : t('elevation.champCode')}
        </label>
        <Input
          id="elevation-code"
          inputMode={secours ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          mono
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        {erreur ? (
          <p className={formStyles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
        <Button variant="ghost" onClick={basculer}>
          {secours ? t('elevation.utiliserCode') : t('elevation.utiliserSecours')}
        </Button>
      </form>
    </Dialog>
  );
}
